import axios from 'axios';

// Airtable API configuration
const BASE_ID = 'appYWNiZNLR8kWV9d';
const TABLE_ID = 'tblUYD4gBWYCS3XpZ';
const PERSONAL_ACCESS_TOKEN = 'pat7Onvdv829Np3E2.cbeaf56462905f8b04fdec13e61891733a82c5887923d59a51e3db0b98ef24f3';
// const PERSONAL_ACCESS_TOKEN = 'patn7g7VNA6ec5CwE.6de523333a425af5fff788eb611fe19839821b98cdfd01884351c530cdf5ef36';
const API_URL = `https://api.airtable.com/v0/${BASE_ID}/${TABLE_ID}`;

// Determine if we're in development or production
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Fetches all records from Airtable
 */
async function fetchFromAirtable() {
  let allRecords = [];
  let offset = null;
  let hasMorePages = true;
  
  while (hasMorePages) {
    const url = offset ? `${API_URL}?offset=${offset}` : API_URL;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${PERSONAL_ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
      },
    });
    
    allRecords = [...allRecords, ...response.data.records];
    
    if (response.data.offset) {
      offset = response.data.offset;
    } else {
      hasMorePages = false;
    }
  }
  
  return allRecords.map(record => {
    let modifiedFields = { ...record.fields };
    
    if (modifiedFields.category) {
      if (typeof modifiedFields.category === 'string') {
        modifiedFields._raw_category = modifiedFields.category;
      }
    }
    
    return {
      id: record.id,
      ...modifiedFields
    };
  });
}

/**
 * Fetches all records from Airtable with progress reporting
 * @param {Function} onProgress - Progress callback function called with current count
 * @returns {Promise<Array>} - Array of records with processed data
 */
async function fetchFromAirtableWithProgress(onProgress) {
  let allRecords = [];
  let offset = null;
  let hasMorePages = true;
  
  // Report initial progress
  if (onProgress && typeof onProgress === 'function') {
    onProgress(0);
  }
  
  while (hasMorePages) {
    const url = offset ? `${API_URL}?offset=${offset}` : API_URL;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${PERSONAL_ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
      },
    });
    
    allRecords = [...allRecords, ...response.data.records];
    
    // Report progress after each page load
    if (onProgress && typeof onProgress === 'function') {
      onProgress(allRecords.length);
    }
    
    if (response.data.offset) {
      offset = response.data.offset;
    } else {
      hasMorePages = false;
    }
  }
  
  return allRecords.map(record => {
    let modifiedFields = { ...record.fields };
    
    if (modifiedFields.category) {
      if (typeof modifiedFields.category === 'string') {
        modifiedFields._raw_category = modifiedFields.category;
      }
    }
    
    return {
      id: record.id,
      ...modifiedFields
    };
  });
}

/**
 * Fetches all records from the Airtable table
 * @returns {Promise<Array>} - Array of records with processed data
 */
export const fetchAllRecords = async () => {
  try {
    if (isDevelopment) {
      // In development, fetch directly from Airtable
      return await fetchFromAirtable();
    } else {
      // In production, use the caching API endpoint
      const response = await axios.get('/api/airtable');
      return response.data.data;
    }
  } catch (error) {
    console.error('Error fetching data from Airtable:', error);
    throw error;
  }
};

/**
 * Fetches all records from the Airtable table with progress reporting
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} - Array of records with processed data
 */
export const fetchAllRecordsWithProgress = async (onProgress) => {
  try {
    if (isDevelopment) {
      // In development, fetch directly from Airtable with progress
      return await fetchFromAirtableWithProgress(onProgress);
    } else {
      // In production, use the caching API endpoint
      // We can't report real progress with the cached endpoint, so we simulate it
      if (onProgress && typeof onProgress === 'function') {
        // Start at 0%
        onProgress(0);
        
        // Simulate progress steps up to about 90%
        const simulateProgress = () => {
          let progress = 0;
          const intervals = [10, 30, 50, 70, 90];
          
          intervals.forEach((targetPercent, index) => {
            setTimeout(() => {
              progress = Math.floor(TOTAL_RECORD_COUNT * (targetPercent / 100));
              onProgress(progress);
            }, 300 * (index + 1)); // Stagger the updates for a smooth effect
          });
        };
        
        // Start simulating progress
        simulateProgress();
      }
      
      // Fetch the actual data
      const response = await axios.get('/api/airtable');
      
      // Call the progress callback with the final count
      if (onProgress && typeof onProgress === 'function') {
        onProgress(response.data.data.length);
      }
      
      return response.data.data;
    }
  } catch (error) {
    console.error('Error fetching data from Airtable:', error);
    throw error;
  }
};

// Hardcoded total count of records to use for progress calculations
export const TOTAL_RECORD_COUNT = 4784;

const airtableUtils = {
  fetchAllRecords,
  fetchAllRecordsWithProgress,
  TOTAL_RECORD_COUNT
};

export default airtableUtils;
